/**
 * <TutorMonthSummary> — « Mon mois », le score et les paiements du masteur en un bloc de chiffres,
 * sur son accueil (organism). Ce qu'il remplace et où chaque chose est restée :
 * `ports/parite-tuteur/coquille-accueil.md` § 5.
 *
 * L'accueil ne garde que ce qu'on lit d'un coup d'œil : le score global et le rang une fois classé, les
 * heures et les euros validés, ce qui attend sa validation (en début de mois, souvent le seul chiffre
 * qui n'est pas zéro), le bonus du Top 3, et ce qui reste à faire sous le seuil d'activité. L'alerte
 * d'assiduité y reste aussi : un signal qui doit changer un comportement ne se cache pas derrière
 * « Détails ».
 *
 * **Rien n'est perdu : tout le détail est à un geste.** Toute la carte ouvre « Détails », une feuille
 * qui pose `<TutorScoreCard>` et `<TutorEarningsCard>` entières. « Mes paiements » reste en tête du bloc.
 *
 * La feuille ouverte est de l'état d'interface : elle vit ici (`initialDetailsOpen` pour une story).
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { List } from "../../molecules/List"
import { Modal } from "../../molecules/Modal"
import { ReferralStatTile } from "../../molecules/ReferralStatTile"
import { TutorEarningsCard } from "../TutorEarningsCard"
import { TutorScoreCard } from "../TutorScoreCard"
import { euros } from "../../../ports/primary/ui/formatters/moneyFormatter"
import {
    hoursLabel,
    PENDING_REVIEW_NOTE,
    pendingEarningsLabel,
    showsPendingReview,
} from "../../../ports/primary/ui/formatters/tutorEarningsFormatter"
import {
    isPunctualityLow,
    percentLabel,
    scoreUnlockLabel,
    showsRank,
} from "../../../ports/primary/ui/formatters/tutorScoreFormatter"
import { type UITutorEarnings, type UITutorScore } from "../../../ports/primary/ui/types/tutorCoquilleAccueil"

const TEXTS = {
    title: "Mon mois",
    payments: "Mes paiements",
    redirecting: "Redirection…",
    details: "Détails",
    detailsTitle: "Mon score et mon mois",
    score: "Mon score",
    lowPunctuality: "Ton assiduité est sous 50\u00A0% : elle pèse sur ton classement",
}

/** L'intitulé du bloc, et de son squelette. */
export const TUTOR_MONTH_SUMMARY_TITLE = TEXTS.title

export type TutorMonthSummaryProps = {
    /** « Mon score ». `null` : pas de chiffres de score. */
    score: UITutorScore | null
    /** Le mois et ses paiements. `null` : pas de chiffres de paiement. */
    earnings: UITutorEarnings | null
    /** « Mes paiements » : le tableau Stripe. Absent, ou sans compte Stripe : pas de lien. */
    onOpenPayments?: () => void
    /** L'adresse du tableau Stripe est demandée : le lien attend. */
    openingPayments?: boolean
    /** « Historique des lives », dans les détails. Absent, ou sans compte Stripe : pas de lien. */
    onOpenLivesHistory?: () => void
    /** Les détails ouverts au montage — pour une story. */
    initialDetailsOpen?: boolean
    testID?: string
}

export function TutorMonthSummary(props: TutorMonthSummaryProps) {
    const {
        score,
        earnings,
        onOpenPayments,
        openingPayments = false,
        onOpenLivesHistory,
        initialDetailsOpen = false,
        testID,
    } = props
    const [detailsOpen, setDetailsOpen] = useState(initialDetailsOpen)

    if (score == null && earnings == null) return null

    const period = score?.periods.current
    const month = earnings?.periods.this_month
    const stripe = earnings?.stripeConnected === true
    const pendingReview = earnings != null && showsPendingReview(earnings, "this_month")
    const unlock =
        score != null && period != null
            ? {
                  label: scoreUnlockLabel(period, score.activityThreshold),
                  progress: period.livesCount > 0 ? (period.livesCount / score.activityThreshold) * 100 : null,
              }
            : null
    const rangeLabel = month?.rangeLabel ?? period?.rangeLabel

    const tiles: { key: string; value: string; label: string }[] = []
    if (period != null && period.performance != null)
        tiles.push({ key: "score", value: percentLabel(period.performance), label: "Score global" })
    if (score != null && period != null && showsRank(period, score.activityThreshold, false))
        tiles.push({ key: "rank", value: `#${period.rank}`, label: "Classement" })
    if (month != null) {
        tiles.push({ key: "hours", value: hoursLabel(month.validatedHours), label: "validées" })
        tiles.push({
            key: "amount",
            value: pendingReview ? `${euros(month.validatedAmount)}*` : euros(month.validatedAmount),
            label: stripe ? "validés" : "validés au total",
        })
    }
    const pendingLabel = stripe && month != null ? pendingEarningsLabel(month.pendingHours, month.pendingAmount) : null
    const lowPunctuality = period != null && isPunctualityLow(period)

    // Quitter l'accueil depuis la feuille la referme : au retour, l'accueil est là, pas la feuille.
    const openLivesHistory =
        onOpenLivesHistory != null
            ? () => {
                  setDetailsOpen(false)
                  onOpenLivesHistory()
              }
            : undefined

    return (
        <>
            <List
                title={TEXTS.title}
                headerRight={
                    stripe && onOpenPayments != null ? (
                        <TextButton
                            label={openingPayments ? TEXTS.redirecting : TEXTS.payments}
                            onPress={onOpenPayments}
                            disabled={openingPayments}
                            size="sm"
                        />
                    ) : undefined
                }
                testID={testID}
            >
                {/* Un bouton dont le lecteur d'écran lit le contenu, « Détails » compris. */}
                <Card p="4" pressable onPress={() => setDetailsOpen(true)}>
                    <Stack gap="3">
                        {tiles.length > 0 && (
                            <View style={tutorMonthSummaryStyles.tiles}>
                                {tiles.map((tile) => (
                                    <View key={tile.key} style={tutorMonthSummaryStyles.tile}>
                                        <ReferralStatTile value={tile.value} label={tile.label} />
                                    </View>
                                ))}
                            </View>
                        )}

                        {pendingLabel != null && (
                            <Text size="text-sm" color="text-muted">
                                {pendingLabel}
                            </Text>
                        )}

                        {lowPunctuality && (
                            <Row gap="2" align="center">
                                <Icon name="exclamation-circle" size={16} color="text-danger" />
                                <Text size="text-sm" weight="bold" color="text-danger" style={styles.shrink}>
                                    {TEXTS.lowPunctuality}
                                </Text>
                            </Row>
                        )}

                        {score?.bonusActive === true && (
                            <Row gap="2">
                                <Badge text="Bonus Top 3 actif" variant="success" tinted iconName="check-circle" />
                            </Row>
                        )}

                        {unlock?.label != null && (
                            <Stack gap="2">
                                <Text size="text-sm" weight="bold" color="text-primary">
                                    {unlock.label}
                                </Text>
                                {unlock.progress != null && (
                                    <ProgressBar value={unlock.progress} color="role" height={6} />
                                )}
                            </Stack>
                        )}

                        {pendingReview && (
                            <Text size="text-xs" color="text-muted">
                                {PENDING_REVIEW_NOTE}
                            </Text>
                        )}

                        <Row gap="2" justify="between" align="center">
                            <Text size="text-xs" color="text-muted" style={styles.shrink}>
                                {rangeLabel ?? ""}
                            </Text>
                            <Row gap="1" align="center">
                                <Text size="text-sm" weight="bold" color="text-link">
                                    {TEXTS.details}
                                </Text>
                                <Icon name="chevron-right" size={16} color="text-link" />
                            </Row>
                        </Row>
                    </Stack>
                </Card>
            </List>

            <Modal
                visible={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                title={TEXTS.detailsTitle}
                fullScreenOnSmall
                dismissable
            >
                <Stack gap="6">
                    {score != null && (
                        <List title={TEXTS.score}>
                            <TutorScoreCard score={score} />
                        </List>
                    )}
                    {earnings != null && (
                        <TutorEarningsCard earnings={earnings} onOpenLivesHistory={openLivesHistory} />
                    )}
                </Stack>
            </Modal>
        </>
    )
}

/** Jusqu'à quatre chiffres sur une rangée ; ils passent à la ligne quand elle est étroite (et le squelette pareil). */
export const tutorMonthSummaryStyles = StyleSheet.create((theme) => ({
    tiles: {
        flexDirection: "row",
        flexWrap: "wrap",
        rowGap: theme.spacing["3"],
        columnGap: theme.spacing["4"],
    },
    tile: {
        flexGrow: 1,
        flexBasis: "20%",
        minWidth: 72,
    },
}))

const styles = StyleSheet.create(() => ({
    shrink: {
        flexShrink: 1,
    },
}))
